import { Injectable } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { deepClone, deepCompareEQ } from '@cf-shared/deep-helpers';
import { AlertController, AlertOptions, AlertButtonRole } from 'providers/alert-controller';
import { TranslatedString } from 'models/translated-string';
import { NavController } from 'providers/nav-controller';

export interface UnsavedChangesGuardOptions<ResultType = void> {
  hasSaveAs?: boolean;
  ignoreMap?: { [key: string]: boolean };
  /** Called after save to get result for returnCallback. If provided, confirmLeaveAsync returns { result } */
  getResult?: () => ResultType;
  /**
   * Optional replacement for the guard's own "has anything changed?" test.
   *
   * By default the guard answers that by comparing the objects it was handed
   * against copies taken when it was built. That only works while the page keeps
   * editing the SAME objects. Some pages replace the thing they are editing
   * while they are open — the equipment editor swaps in a whole new draft when
   * the user picks a different machine template or applies a scan result — and a
   * guard still holding the previous object would compare the wrong thing and
   * silently answer "nothing changed".
   *
   * Such a page passes its own test here and an empty `objectsToWatch`.
   */
  hasUnsavedChanges?: () => boolean;
}

/**
 * The page's save. It may answer `false` to say it saved NOTHING and the user should stay
 * on the page — for example when the page asked a question first and the user chose to
 * keep editing (#6268). Answering nothing means it saved.
 */
export type UnsavedChangesGuardCallback = (saveAs?: boolean) => Promise<void | false>;

/** Return type for confirmLeaveAsync - allows passing result via ionViewCanLeave */
export type CanLeaveResult<T> = boolean | { result: T };

export class UnsavedChangesGuard<ResultType = void> {
  private objectsToWatch_org: any[] = [];
  private markedForSaveOnExit = false;

  constructor(
    private objectsToWatch: any[],
    private saverAsync: UnsavedChangesGuardCallback,
    private opts: UnsavedChangesGuardOptions<ResultType>,
    private alertCtrl: AlertController,
    private navCtrl: NavController
  ) {
    this.recordOriginal();
  }

  private recordOriginal() {
    this.objectsToWatch_org = [];
    this.objectsToWatch.forEach(o => this.objectsToWatch_org.push(deepClone(o)));
  }

  // call this to save manually (usually before exit). Answers false when the page saved nothing.
  async applyChangesAsync(saveAs?: boolean): Promise<boolean> {
    const answer = await this.saverAsync(saveAs);
    // if the page declined to save, its edits are still unsaved — keep the old snapshots
    if (answer === false) return false;
    this.recordOriginal();
    return true;
  }

  /** When exiting, we will save without asking the user. To be used for "Save and exit" buttons.
   * NOTE: The only way to force saving is on actual exit. Otherwise it would be easy to
   * get into a situation where data is saved and then this detects more some changes before actual exit
   * then asks the user what to do - which is weird because the user has just clicked on "Save and exit".
   */
  public markForSaveOnExit() {
    this.markedForSaveOnExit = true;
  }

  /** Call after saving outside the guard (e.g., page's Save button) to update snapshots */
  public updateSnapshots() {
    this.recordOriginal();
  }

  /** Save changes and exit the page. Use this for "Save" or "Save and exit" buttons.
   * @param shouldPop Optional guard — if provided, pop is skipped when it returns false
   *                  (e.g. when the user already navigated away while the save was in progress).
   */
  public async saveAndExitAsync(shouldPop?: () => boolean): Promise<boolean> {
    // if the page saved nothing, the user stays on it
    if (!await this.applyChangesAsync()) return false;
    if (!shouldPop || shouldPop()) {
      await this.navCtrl.popAsync(this.opts?.getResult?.());
    }
    return true;
  }

  /** Helper to return result if getResult is configured, otherwise just true */
  private leaveWithResult(): CanLeaveResult<ResultType> {
    if (this.opts?.getResult) {
      return { result: this.opts.getResult() };
    }
    return true;
  }

  /** Whether anything the guard is responsible for has changed since it was built. */
  private hasChanges(): boolean {
    // the page answers this itself — see hasUnsavedChanges in the options
    if (this.opts?.hasUnsavedChanges) return this.opts.hasUnsavedChanges();

    for (let i = 0; i < this.objectsToWatch.length; i++) {
      if (!deepCompareEQ(this.objectsToWatch[i], this.objectsToWatch_org[i], this.opts && this.opts.ignoreMap)) {
        return true;
      }
    }
    return false;
  }

  // call this on ionViewCanLeave()
  public async confirmLeaveAsync(isValid?: boolean): Promise<CanLeaveResult<ResultType>> {
    if (!this.hasChanges()) {
      // no unsaved changes - but still return result if configured (user may have already saved)
      return this.leaveWithResult();
    }

    if (this.markedForSaveOnExit) {
      this.markedForSaveOnExit = false; // in case this somehow gets reentered
      if (isValid === false) {
        return false;
      } else {
        if (!await this.applyChangesAsync(false)) return false;
        return this.leaveWithResult();
      }
    }

    let opts: AlertOptions<'save' | 'save-as' | 'discard' | false> = {
      title: LL.unsavedChanges.title(),
      message:
        isValid === false
          ? LL.unsavedChanges.alerts.incompleteData()
          : LL.unsavedChanges.alerts.applyChanges(),
      implicitCancelResult: false,
      buttons: [
        { text: LL.common.buttons.discard(), result: 'discard' },
      ],
    };

    // if data is valid, show Save button with Primary role
    if (isValid !== false) {
      opts.buttons.push({
        text: LL.common.buttons.save(),
        role: AlertButtonRole.Primary,
        result: 'save',
      });
    } else {
      // if data is invalid, Discard becomes primary (only action available)
      opts.buttons[0].role = AlertButtonRole.Primary;
    }

    if (this.opts && this.opts.hasSaveAs) {
      opts.buttons.push({
        text: LL.common.buttons.saveAsNew(),
        result: 'save-as',
      });
    }

    let choice = await this.alertCtrl.showAlertAsync(opts);

    // if cancelled (backdrop/X button), block navigation
    if (choice === false) return false;

    // if save was selected, apply changes and return with result
    // if the page then declines to save, the user stays on it
    if (choice === 'save') {
      if (!await this.applyChangesAsync(false)) return false;
      return this.leaveWithResult();
    }
    if (choice === 'save-as') {
      if (!await this.applyChangesAsync(true)) return false;
      return this.leaveWithResult();
    }

    // discard — allow leaving without saving
    return true;
  }
}

/**
 * Ask before leaving a page whose unsaved work CANNOT be saved on the way out.
 *
 * The guard above offers Save or Discard, which is right for a form with a Save
 * button behind it. It is meaningless for an unsent message or a half-finished
 * wizard: there is nothing to save the work INTO, so the only real question is
 * whether the user meant to throw it away.
 *
 * Returns true when the page may close. Answering the dialog's dismiss control
 * (its backdrop or X) counts as "stay", the same way the save-or-discard dialog
 * treats it — a dismissed question has not been answered, and the safe reading
 * of an unanswered question is to keep the user's work.
 *
 * `title` and `message` are the page's own words, because "your message" and
 * "your check-up answers" are not interchangeable to a reader.
 */
export async function confirmDiscardAsync(
  alertCtrl: AlertController,
  texts: { title: TranslatedString, message: TranslatedString },
): Promise<boolean> {
  const choice = await alertCtrl.showAlertAsync<'discard' | false>({
    title: texts.title,
    message: texts.message,
    implicitCancelResult: false,
    buttons: [
      { text: LL.common.buttons.discard(), role: AlertButtonRole.Primary, result: 'discard' },
      { text: LL.unsavedChanges.buttons.keepEditing(), role: AlertButtonRole.Cancel, result: false },
    ],
  });
  return choice === 'discard';
}

@Injectable()
export class UnsavedChangesGuardProvider {
  constructor(
    private alertCtrl: AlertController,
    private navCtrl: NavController
  ) { }

  createGuard<ResultType = void>(objectsToWatch: any[], saverAsync: UnsavedChangesGuardCallback, opts?: UnsavedChangesGuardOptions<ResultType>) {
    return new UnsavedChangesGuard<ResultType>(objectsToWatch, saverAsync, opts, this.alertCtrl, this.navCtrl);
  }
}
